import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { checkRouteRestore } from '@/api/api'
import { confirmDialog } from '@/lib/confirm-dialog'
import { PATHS } from '@/routes/paths'

const STORAGE_KEY = 'pml-last-route'

/** 復元対象外のルート */
const EXCLUDED_PATHS = [PATHS.SIGN_OUT, PATHS.SHARE]

type SavedRoute = {
  path: string
}

/** オンライン/オフラインでツリーが差し替わっても、1 プロセスで 1 回だけ尋ねる */
let hasPrompted = false
/** 尋ねている間は保存しない（答える前に kill されても戻り先を残す） */
let isAsking = false

/** プロセス再起動時のルート復元で開かれた画面か（自動再生などを控えるため） */
export const isRestoredRoute = (state: unknown): boolean =>
  typeof state === 'object' && state !== null && 'routeRestored' in state

const saveRoute = (pathname: string, search: string) => {
  if (EXCLUDED_PATHS.some((excluded) => pathname.startsWith(excluded))) return
  const data: SavedRoute = { path: pathname + search }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
}

const readSavedPath = (): string | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const saved: unknown = JSON.parse(raw)
    if (typeof saved !== 'object' || saved === null || typeof (saved as SavedRoute).path !== 'string') return null
    return (saved as SavedRoute).path
  } catch {
    return null
  }
}

/** Safe モードで隠すドメインのページは尋ねない。判定は BE に任せ、確認できなければ尋ねない */
const promptRestore = async (path: string): Promise<boolean> => {
  const check = await checkRouteRestore(path).catch(() => null)
  if (!check?.can_prompt || window.location.pathname !== '/') return false
  return confirmDialog({
    title: '前回の続きを開きますか？',
    description: check.label ?? path,
    imageSrc: check.thumbnail_url ? `/api${check.thumbnail_url}` : undefined,
    confirmLabel: '開く',
  })
}

/**
 * iOSのPWAバックグラウンドkill対策:
 * 現在のルートをlocalStorageに保存し、プロセス再起動時に戻るかをダイアログで尋ねる
 */
export function useRouteRestore() {
  const location = useLocation()
  const navigate = useNavigate()

  // 起動時に保存ルートへ戻るか尋ねる（mount時1回のみ）
  useEffect(() => {
    if (hasPrompted) return
    hasPrompted = true

    // ディープリンクやブックマークからのアクセスは上書きしない
    if (location.pathname !== '/') return

    // 直後の保存 effect が走る前に読む
    const path = readSavedPath()
    if (!path || path === '/') return

    isAsking = true
    void promptRestore(path).then((ok) => {
      isAsking = false
      if (ok) {
        navigate(path, { replace: true, state: { routeRestored: true } })
        return
      }
      saveRoute(window.location.pathname, window.location.search)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ルート変更時にlocalStorageへ保存
  useEffect(() => {
    if (isAsking) return
    saveRoute(location.pathname, location.search)
  }, [location.pathname, location.search])
}
